
    import Vue from "vue";
    import Component from "vue-class-component";
    import { Ref, Watch } from "vue-property-decorator";
    import { VForm } from "@/vForm";
    import * as toastr from "toastr";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import DualListBox from "@/vue/components/DualListBox.vue";
    import ConfirmDialogue from "@/vue/components/ConfirmDialogue.vue";
    import ApiClient from "@/utilities/ApiClient";
    import Utils from "@/utilities/Utils";
    import { ISaveResponse } from "@/model/ISaveResponse";
    import { UserRole } from "@/model/Enums";
    import { ILookupItem, LookupItem } from "@/model/LookupItem";
    import { IUser, User } from "@/model/User";
    import { IClientLookup } from "@/model/Client";
    import { ISiteLookup } from "@/model/Site";

    @Component({ components: { ApiButton, ConfirmDialogue, DualListBox } })
    export default class UserDialogue extends Vue {

        async mounted(): Promise<void> {
            this.clientList = await ApiClient.get(Utils.addClientQueryParameters("/api/client/lookups"));
            this.siteList = await ApiClient.get(Utils.addClientQueryParameters("/api/site/lookups"));
        }

        //
        // -- properties
        //

        // so we can use the enum in template
        userRole: any = UserRole;

        @Ref("userForm") private readonly userForm!: VForm;
        showDialogue: boolean = false;
        dialogueTitle: string = "";
        clientList: Array<IClientLookup> = [];
        get clientOptions(): Array<IClientLookup> { return Utils.clientOptionsFilter(this.clientList); }
        siteList: Array<ISiteLookup> = [];
        get siteOptions(): Array<ISiteLookup> { return Utils.siteOptionsFilter(this.siteList, this.user.clientID ); }
        pathwayList: Array<ILookupItem> = [];
        sponsorList: Array<ILookupItem> = [];

        user: User = new User();

        get roleList(): Array<ILookupItem> {
            // this depends on lesser roles being numerically greater!
            return Utils.enumToLookups(UserRole).filter(r => r.id === 0 || r.id >= this.$store.state.signedInUser.role);
        }

        get saveButtonText(): string {
            return Utils.hasDateValue(this.user.deleted) ? "Save and un-archive" : "Save";
        }

        get isEditingSelf(): boolean {
            if (!this.user) return false;
            if (!this.$store.state.signedInUser) return false;
            return this.user.id === this.$store.state.signedInUser.id;
        }

        get isClientRole(): boolean {
            return this.user.role === UserRole.Client;
        }

        get isSiteRole(): boolean {
            return this.user.role === UserRole.Site
                || this.user.role === UserRole.SitePathways
                || this.user.role === UserRole.SiteSponsors;
        }

        get canSeeUserAdminOption(): boolean {
            return this.user.role === UserRole.Site
                || this.user.role === UserRole.Client;
            // don't show for Admin - they can edit users by default
        }

        get canChangeClient(): boolean {
            return (this.$store.state.signedInUser as User|null)?.canFilterClient ?? false;
        }

        get canChangeSite(): boolean {
            return this.$store.state.signedInUser.role == UserRole.Admin
                || this.$store.state.signedInUser.role == UserRole.Client;
        }

        get canDelete(): boolean {
            return !this.user.isNew && !Utils.hasDateValue(this.user.deleted);
        }

        //
        // -- validations
        //

        validateClientIDs (value: Array<string>): string|boolean {
            if(value.length > 0 || !this.canChangeClient) return true;
//console.log("### fail validateClientIDs");
            return "Please select at least one client";
        }

        validateSiteID (value: number): string|boolean {
            if(Boolean(value) || !this.isSiteRole) return true;
//console.log("### fail validateSiteIDs");
            return "Please choose a site";
        }

        validatePathways (value: Array<string>): string|boolean {
            if(value.length > 0 || this.user.role !== UserRole.SitePathways) return true;
//console.log("### fail validatePathways");
            return "Please choose one or more pathways";
        }

        validateSponsors (value: Array<string>): string|boolean {
            if(value.length > 0 || this.user.role !== UserRole.SiteSponsors) return true;
//console.log("### fail validateSponsors");
            return "Please choose one or more sponsors";
        }

        emailErrorMessages: Array<string> = [];

        @Watch("user.email")
        async validateEmail(): Promise<void> {
            this.emailErrorMessages = [];
            if (Utils.isEmptyOrWhitespace(this.user.email))  return;

            const response: { isInUse: boolean } = await ApiClient.get(`/api/user/emailIsInUse?email=${this.user.email}&userID=${this.user.id}`);
            if (response.isInUse) {
                this.emailErrorMessages.push("Email address is already in use");
                return;
            }
        }

        //
        // -- methods
        //

        @Watch("user.clientIDs")
        async onClientChanged(): Promise<void> {
            if(!this.user || this.user.clientIDs.length == 0) {
                this.siteList = [];
                return;
            }
            this.siteList = await ApiClient.get(Utils.addClientQueryParameters("/api/site/lookups"));
        }

        @Watch("user.siteID")
        async onSiteChanged(): Promise<void> {
            const siteID = this.user.siteID || Utils.emptyGuidValue;
            if(Utils.isEmptyId(siteID)) {
                this.pathwayList = [];
                this.sponsorList = [];
                return;
            }
            const loadPathways = async () => { this.pathwayList = await ApiClient.get(`/api/Pathway/lookups?siteID=${siteID}`); }
            const loadSponsors = async () => { this.sponsorList = await ApiClient.get(`/api/siteRecipient/lookups?siteID=${siteID}&justSponsors=1`); }
            await Promise.all([
                loadPathways(),
                loadSponsors()
            ]);
        }

        add(): void {
            this.reset();
            this.dialogueTitle = "Add User";
            this.showDialogue = true;
        }

        async edit(id: string): Promise<void> {
            this.reset();
            this.dialogueTitle = "Edit User";
            const userData: IUser = await ApiClient.get(`api/user/Load?id=${id}`);
            this.user.update(userData);
            this.showDialogue = true;
        }

        reset(): void {
            Utils.resetObject(this.user);
            this.user.id = Utils.emptyGuidValue;
            const signedInUser = this.$store.state.signedInUser;
            if (signedInUser.role === UserRole.Site) {
                this.user.clientIDs = [];
                this.user.siteID = signedInUser.siteID;
            }
            // if signedInUser only has one client ID, there is no choice so set new user clientID automatically
            if (signedInUser.role === UserRole.Client && this.user.clientIDs.length === 1) {
                this.user.clientIDs = [... signedInUser.clientIDs];
            }
            this.userForm?.resetValidation();
            this.emailErrorMessages = [];
        }

        async save(): Promise<void> {
            await this.validateEmail();

            const validationResult = this.userForm.validate();

console.log("this.emailErrorMessages ", this.emailErrorMessages);
console.log("validationResult ", validationResult);

            const isValid = validationResult && this.emailErrorMessages.length === 0;
            if (!isValid) {
                toastr.warning("Please fix highlighted issues", "Cannot Save");
                return;
            }
            const response: ISaveResponse = await ApiClient.post("/api/user/save", this.user, "user-save");
            if(this.user.isNew) this.user.id = response.newID;
            toastr.success("Saved");
            this.$emit("saved");
            this.showDialogue = false;
        }

        confirmDelete(): void {
            const dialog: ConfirmDialogue = this.$refs.deleteDialogue as ConfirmDialogue;
            dialog.show();
        }

        async doDelete(): Promise<void> {
            await ApiClient.post("/api/user/delete", this.user, "user-delete");
            toastr.warning("Deleted");
            this.$emit("saved");
            this.showDialogue = false;
        }
    }
